import { wabunkaCancellationPolicyTriggerToJSON } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_cancellation_policy_trigger';
import { WabunkaExperienceEventReservationType, wabunkaExperienceEventReservationTypeFromJSON } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event_reservation_type';
import dayjs from 'dayjs';

import { buildWabunkaExperienceViewModel, createDefaultWabunkaExperienceViewModel, type WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { buildWabunkaExperienceEventInterpreterOptionListViewModel, type WabunkaExperienceEventInterpreterOptionViewModel } from '@/models/wabunka/wabunkaExperienceEventInterpreterOption';
import { buildWabunkaExperienceEventOptionListViewModel, type WabunkaExperienceEventOptionViewModel } from '@/models/wabunka/wabunkaExperienceEventOption';
import { formatFromDateTimeInEnLocale, formatFromToDateTimeInEnLocale, formatTime } from '@/utils/date';
import { calcPriceIncludingTax } from '@/utils/priceIncludingTax';
import { isOvernightStay } from '@/wabunka-src/features/Experience';
import type { WabunkaCancellationPolicyTrigger } from '@/wabunka-src/features/Experience/models/cancellationPolicyTrigger';
import { buildWabunkaExperienceEventFeeListViewModel, type WabunkaExperienceEventFeeViewModel } from '@/wabunka-src/features/Experience/models/experienceEventFee';

import type { WabunkaExperienceEvent } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event';

/**
 * 体験イベントビューモデル
 */
export interface WabunkaExperienceEventViewModel {
  /** ID */
  id: string;
  /** 開始日時 */
  startAt: Date;
  /** 終了日時 */
  endAt: Date;
  /** 予約締切（N時間前） */
  bookingLeadHours: number;
  /** キャンセルポリシー適用トリガー */
  cancellationPolicyTrigger: WabunkaCancellationPolicyTrigger;
  /** キャンセル・変更締切（N時間前） */
  confirmedBookingLeadHours?: number | undefined;
  /** 予約タイプ */
  reservationType: WabunkaExperienceEventReservationType;
  /** 体験マスタのリクエスト予約切り替え時間を考慮した予約タイプ */
  effectiveReservationType?: WabunkaExperienceEventReservationType;
  /** 予約可否 */
  canReserve: boolean;
  /** 金額（税抜） */
  price: number;
  /** 税率 */
  taxRate: number;
  /** 金額(税込) */
  priceIncludingTax: number;
  /** 受入可能人数 */
  maxGuests: number;
  /** 最少予約人数 */
  minGuestsPerReservation: number;
  /** 体験 */
  experience: WabunkaExperienceViewModel;
  /** 体験イベントオプション */
  experienceEventOptions: WabunkaExperienceEventOptionViewModel[];
  /** 通訳オプション */
  interpreterOptions: WabunkaExperienceEventInterpreterOptionViewModel[];
  /** 時間枠ID */
  timeSlotId: string;
  /** 体験イベント料金 */
  experienceEventFees?: WabunkaExperienceEventFeeViewModel[];
}

/**
 * 体験イベント用ビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaExperienceEventViewModel} - 体験イベント用ビューモデルのデフォルト値
 */
export const createDefaultWabunkaExperienceEventViewModel = (): WabunkaExperienceEventViewModel => ({
  id: '',
  startAt: new Date(),
  endAt: new Date(),
  bookingLeadHours: 0,
  cancellationPolicyTrigger: 'CONFIRMED_BOOKING_LEAD_HOURS',
  confirmedBookingLeadHours: 0,
  reservationType: WabunkaExperienceEventReservationType.EXPERIENCE_EVENT_RESERVATION_TYPE_REQUEST,
  effectiveReservationType: undefined,
  canReserve: false,
  price: 0,
  taxRate: 0,
  priceIncludingTax: 0,
  maxGuests: 0,
  minGuestsPerReservation: 0,
  experience: createDefaultWabunkaExperienceViewModel(),
  experienceEventOptions: [],
  interpreterOptions: [],
  timeSlotId: '',
  experienceEventFees: [],
});
/**
 * APIレスポンスからビューモデルを作成する
 *
 * @param {WabunkaExperience} res - APIレスポンス
 * @returns {WabunkaExperienceEventViewModel} - ビューモデル
 */
export const buildWabunkaExperienceEventViewModel = (res: WabunkaExperienceEvent | undefined): WabunkaExperienceEventViewModel => {
  if (res == null) {
    return createDefaultWabunkaExperienceEventViewModel();
  }
  return {
    id: res.id,
    startAt: dayjs(res.startAt).toDate(),
    endAt: dayjs(res.endAt).toDate(),
    bookingLeadHours: res.bookingLeadHours,
    cancellationPolicyTrigger: wabunkaCancellationPolicyTriggerToJSON(res.cancellationPolicyTrigger) as WabunkaCancellationPolicyTrigger,
    confirmedBookingLeadHours: res.confirmedBookingLeadHours,
    reservationType: wabunkaExperienceEventReservationTypeFromJSON(res.reservationType),
    effectiveReservationType: res.effectiveReservationType != null ? wabunkaExperienceEventReservationTypeFromJSON(res.effectiveReservationType) : undefined,
    taxRate: res.taxRate,
    price: res.price,
    maxGuests: res.maxGuests,
    minGuestsPerReservation: res.minGuestsPerReservation,
    canReserve: res.canReserve,
    experience: buildWabunkaExperienceViewModel(res.experience),
    priceIncludingTax: calcPriceIncludingTax(res.price, res.taxRate),
    experienceEventOptions: buildWabunkaExperienceEventOptionListViewModel(res.experienceEventOptions),
    interpreterOptions: buildWabunkaExperienceEventInterpreterOptionListViewModel(res.experienceEventInterpreterOptions),
    experienceEventFees: buildWabunkaExperienceEventFeeListViewModel(res.experienceEventFees),
    timeSlotId: res.timeSlotId ?? '',
  };
};

/**
 * リスト表示用のビューモデル
 */
export type WabunkaExperienceEventListViewModel = WabunkaExperienceEventViewModel[];

/**
 * { [YYYY-MM-DD]: WabunkaExperienceEventViewModel }
 */
export type WabunkaExperienceEventDictViewModel = Record<string, WabunkaExperienceEventListViewModel>;

/**
 * リスト表示用のビューモデルを組み立てる
 */
export const buildWabunkaExperienceEventListViewModel = (experienceEvents: WabunkaExperienceEvent[] | undefined): WabunkaExperienceEventListViewModel => {
  if (!experienceEvents) {
    return [];
  }

  return experienceEvents.map(buildWabunkaExperienceEventViewModel);
};

/**
 * DailyWabunkaExperienceEvents配列からカレンダー表示用のビューモデルを組み立てる
 */
export const buildWabunkaExperienceEventDictViewModelFromDaily = (dailyList: { date: string; events: WabunkaExperienceEvent[] }[] | undefined): WabunkaExperienceEventDictViewModel => {
  if (!dailyList) {
    return {};
  }

  return dailyList.reduce<WabunkaExperienceEventDictViewModel>((dict, daily) => {
    // カレンダー側で使用する 'YYYY-MM-DD' に正規化する
    const normalizedDateKey = /^\d{8}$/.test(daily.date) ? `${daily.date.slice(0, 4)}-${daily.date.slice(4, 6)}-${daily.date.slice(6, 8)}` : daily.date;
    dict[normalizedDateKey] = buildWabunkaExperienceEventListViewModel(daily.events);
    return dict;
  }, {});
};

export interface WabunkaCandidateEventOption {
  label: string;
  value: string;
  disabled: boolean;
  reservationType: WabunkaExperienceEventReservationType;
}

// イベント選択肢表示用のビューモデルを組み立てる
export const buildWabunkaCandidateEventOptionViewModel = (experienceEvents: WabunkaExperienceEventListViewModel | undefined): WabunkaCandidateEventOption[] => {
  if (!experienceEvents) {
    return [];
  }
  return experienceEvents.map((e) => {
    const startTime = formatTime(e.startAt ?? new Date(), 'en');
    const label = isOvernightStay(e.experience) ? startTime : `${startTime} - ${formatTime(e.endAt ?? new Date(), 'en')}`;
    return {
      label,
      value: e.id,
      disabled: !e.canReserve,
      reservationType: e.effectiveReservationType ?? e.reservationType,
    };
  });
};

// 体験イベントの参加希望日時を返却する
export const buildWabunkaExperienceEventPreferredDateTime = (experienceEvent: WabunkaExperienceEventViewModel): string => {
  return isOvernightStay(experienceEvent.experience) ? formatFromDateTimeInEnLocale(experienceEvent.startAt) : formatFromToDateTimeInEnLocale(experienceEvent.startAt, experienceEvent.endAt);
};

/**
 * 予約体験イベントビューモデル
 * 予約完了後にreservationオブジェクトに含まれる体験イベント情報
 */
export interface WabunkaReservationExperienceEventViewModel {
  /** ID */
  id: string;
  /** 予約タイプ */
  reservationType: WabunkaExperienceEventReservationType;
}

/**
 * 予約体験イベント用ビューモデルのデフォルト値を返却する
 *
 * @returns {WabunkaReservationExperienceEventViewModel} - 予約体験イベント用ビューモデルのデフォルト値
 */
export const createDefaultWabunkaReservationExperienceEventViewModel = (): WabunkaReservationExperienceEventViewModel => ({
  id: '',
  reservationType: WabunkaExperienceEventReservationType.EXPERIENCE_EVENT_RESERVATION_TYPE_UNKNOWN,
});

/**
 * APIレスポンスから予約体験イベントビューモデルを作成する
 *
 * @param {WabunkaExperienceEvent} res - APIレスポンス
 * @returns {WabunkaReservationExperienceEventViewModel} - ビューモデル
 */
export const buildWabunkaReservationExperienceEventViewModel = (res: WabunkaExperienceEvent | undefined): WabunkaReservationExperienceEventViewModel => {
  if (!res) {
    return createDefaultWabunkaReservationExperienceEventViewModel();
  }
  return {
    id: res.id,
    reservationType: wabunkaExperienceEventReservationTypeFromJSON(res.reservationType),
  };
};
